import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, Loader2, Sparkles, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/gestione/costi/grammature`;
const UNITA = ['g', 'ml', 'pz'];
const PASSO = 15;

const RigaGrammi = ({ ricettaId, r, onCambia }) => {
  const vuota = r.quantita === '' || r.quantita == null;
  return (
    <div className="grid grid-cols-[1fr_auto_auto] items-center gap-2 py-1.5" data-testid={`grammi-riga-${ricettaId}-${r.idx}`}>
      <div className="min-w-0">
        <p className="text-sm text-gray-900 truncate">{r.nome}</p>
        <p className="text-[11px] text-gray-400">ora {r.attuale ?? '—'} {r.unita_attuale} {vuota ? '· da inserire' : ''}</p>
      </div>
      <Input type="number" min="0" step="1" value={r.quantita ?? ''} onChange={(e) => onCambia({ quantita: e.target.value })}
        className={`h-8 w-20 text-right ${vuota ? 'border-amber-400' : ''}`} data-testid={`grammi-quantita-${ricettaId}-${r.idx}`} />
      <select value={r.unita} onChange={(e) => onCambia({ unita: e.target.value })} data-testid={`grammi-unita-${ricettaId}-${r.idx}`}
        className="h-8 rounded-md border border-gray-200 bg-white px-1.5 text-sm text-gray-900">
        {UNITA.map((u) => <option key={u} value={u}>{u}</option>)}
      </select>
    </div>
  );
};

const SchedaRicetta = ({ p, righe, onRiga, onConferma, onScarta, occupato }) => (
  <li className="rounded-xl border border-gray-200 p-3" data-testid={`grammi-ricetta-${p.ricetta_id}`}>
    <div className="flex items-start justify-between gap-2 mb-1">
      <div>
        <p className="text-sm font-semibold text-gray-900">{p.nome}</p>
        <p className="text-xs text-gray-500">{p.categoria || 'Senza categoria'} · per 1 porzione, peso come acquistato</p>
      </div>
      <div className="flex gap-1 shrink-0">
        <Button size="sm" variant="ghost" className="h-8 text-gray-500" disabled={occupato} onClick={() => onScarta(p.ricetta_id)} data-testid={`grammi-scarta-${p.ricetta_id}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" disabled={occupato} onClick={() => onConferma(p.ricetta_id)} data-testid={`grammi-conferma-${p.ricetta_id}`}>
          <Check className="h-3.5 w-3.5 mr-1" /> Conferma
        </Button>
      </div>
    </div>
    <div className="divide-y divide-gray-50">
      {righe.map((r, i) => <RigaGrammi key={r.idx} ricettaId={p.ricetta_id} r={r} onCambia={(v) => onRiga(p.ricetta_id, i, v)} />)}
    </div>
  </li>
);

const Avanzamento = ({ job }) => {
  const pct = job.totale ? Math.round(((job.fatti + job.falliti) / job.totale) * 100) : 0;
  return (
    <div className="rounded-lg bg-blue-50 p-3" data-testid="grammi-avanzamento">
      <p className="flex items-center gap-2 text-sm text-blue-800"><Loader2 className="h-4 w-4 animate-spin" /> L'AI sta pesando {job.fatti + job.falliti} di {job.totale} ricette…</p>
      <div className="mt-2 h-1.5 rounded-full bg-blue-100 overflow-hidden"><div className="h-full bg-blue-600 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

const perInvio = (righe) => righe.map(({ idx, quantita, unita }) => ({ idx, unita, quantita: quantita === '' || quantita == null ? null : Number(quantita) }));

export default function GrammatureAI({ headers }) {
  const [dati, setDati] = useState(null);
  const [valori, setValori] = useState({});
  const [occupato, setOccupato] = useState(false);
  const [mostra, setMostra] = useState(PASSO);

  const carica = useCallback(async () => {
    try {
      const { data } = await axios.get(API, { headers: headers() });
      setDati(data);
      setValori((prima) => Object.fromEntries(data.proposte.map((p) => [p.ricetta_id, prima[p.ricetta_id] || p.righe.map((r) => ({ ...r }))])));
    } catch (e) {
      toast.error('Grammature non disponibili');
    }
  }, [headers]);

  useEffect(() => { carica(); }, [carica]);
  const inCorso = dati?.job?.stato === 'in_corso';
  useEffect(() => {
    if (!inCorso) return undefined;
    const t = setInterval(carica, 3000);
    return () => clearInterval(t);
  }, [inCorso, carica]);

  const esegui = async (fn, messaggio) => {
    setOccupato(true);
    try {
      const { data } = await fn();
      toast.success(typeof messaggio === 'function' ? messaggio(data) : messaggio);
      await carica();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Operazione non riuscita');
    } finally {
      setOccupato(false);
    }
  };

  const avvia = () => {
    if (dati?.proposte?.length && !window.confirm('Le proposte non ancora confermate verranno rifatte. Continuare?')) return;
    esegui(() => axios.post(`${API}/avvia`, {}, { headers: headers() }), (d) => `Proposta avviata per ${d.totale} ricette`);
  };
  const conferma = (id) => esegui(() => axios.put(`${API}/${id}`, { righe: perInvio(valori[id]) }, { headers: headers() }), (d) => `Salvate ${d.salvate} grammature`);
  const scarta = (id) => esegui(() => axios.delete(`${API}/${id}`, { headers: headers() }), 'Proposta scartata');
  const confermaTutte = () => {
    if (!window.confirm(`Salvare le grammature di ${dati.proposte.length} ricette?`)) return;
    const ricette = Object.entries(valori).map(([ricetta_id, righe]) => ({ ricetta_id, righe: perInvio(righe) }));
    esegui(() => axios.post(`${API}/conferma-tutte`, { ricette }, { headers: headers() }), (d) => `Salvate ${d.salvate} grammature in ${d.ricette} ricette`);
  };
  const cambiaRiga = (id, i, v) => setValori((prima) => ({ ...prima, [id]: prima[id].map((r, j) => (j === i ? { ...r, ...v } : r)) }));

  if (!dati) return <p className="flex items-center gap-2 text-sm text-gray-500 py-6" data-testid="grammi-caricamento"><Loader2 className="h-4 w-4 animate-spin" /> Carico…</p>;
  const proposte = dati.proposte;
  return (
    <div className="rounded-2xl bg-white border border-gray-200 p-4 space-y-3" data-testid="grammature-ai">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Grammature per porzione</h2>
          <p className="text-xs text-gray-500" data-testid="grammi-riepilogo">
            {dati.riepilogo.da_stimare} ricette su {dati.riepilogo.ricette} hanno grammi da inserire · {proposte.length} proposte da rivedere · niente si salva senza la tua conferma
          </p>
        </div>
        <Button size="sm" onClick={avvia} disabled={occupato || inCorso || !dati.riepilogo.da_stimare} className="bg-blue-600 hover:bg-blue-700" data-testid="grammi-avvia">
          <Sparkles className="h-4 w-4 mr-1" /> Proponi grammature per tutte
        </Button>
      </div>
      {inCorso && <Avanzamento job={dati.job} />}
      {dati.job?.stato === 'completato' && dati.job.falliti > 0 && (
        <p className="text-xs text-amber-700" data-testid="grammi-falliti">{dati.job.falliti} ricette non elaborate: riprova più tardi.</p>
      )}
      {proposte.length > 0 && !inCorso && (
        <Button onClick={confermaTutte} disabled={occupato} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="grammi-conferma-tutte">
          <Check className="h-4 w-4 mr-1" /> Conferma tutte ({proposte.length} ricette)
        </Button>
      )}
      <ul className="space-y-2" data-testid="grammi-elenco">
        {proposte.slice(0, mostra).map((p) => (
          <SchedaRicetta key={p.ricetta_id} p={p} righe={valori[p.ricetta_id] || p.righe} onRiga={cambiaRiga} onConferma={conferma} onScarta={scarta} occupato={occupato} />
        ))}
      </ul>
      {!proposte.length && !inCorso && <p className="py-4 text-sm text-gray-500" data-testid="grammi-vuoto">Nessuna proposta da rivedere.</p>}
      {proposte.length > mostra && (
        <Button variant="ghost" className="w-full" onClick={() => setMostra(mostra + PASSO)} data-testid="grammi-altre">Mostra altre ({proposte.length - mostra})</Button>
      )}
    </div>
  );
}
